import { Injectable, inject, WritableSignal } from '@angular/core';
import { ILesson, LessonImage, LESSONS_TOKEN, DynamicContentComponent, ContentMetadata } from '../components/lesson-mini-components/components/lessons.clases'; // Added DynamicContentComponent, ContentMetadata
import { LoadingBarService, TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core';
import { ChatRole, CONVERSATION_AI_TOKEN } from '@dataclouder/ngx-agent-cards';

// import { UserDataExchangeService } from '@dataclouder/ngx-agent-cards';

@Injectable({
  providedIn: 'root', // Provide globally or in a specific module if preferred
})
export class LessonUtilsService {
  #lessonService = inject(LESSONS_TOKEN);
  #toastService = inject(TOAST_ALERTS_TOKEN);
  #agentService = inject(CONVERSATION_AI_TOKEN);

  loadingBarService = inject(LoadingBarService);

  /**
   * Validates if audios need generation for the given lesson.
   * Currently logs a placeholder message.
   * @param lesson The lesson object (or signal) to validate.
   */
  public validateAudios(lesson: ILesson | undefined): void {
    // Access lesson data directly
    if (!lesson?.components) {
      return;
    }
    // Placeholder logic - adapt as needed from original component
    console.log('Validating audios for lesson:', lesson.id);
  }

  /**
   * Updates the lesson signal with a new banner image in metadata.
   * @param lessonSignal The signal holding the lesson data.
   * @param imageUploaded The image data object from the upload event. Should conform to LessonImage structure partially.
   */
  public uploadCover(lessonSignal: WritableSignal<ILesson | undefined>, imageUploaded: any): void {
    lessonSignal.update((currentLesson) => {
      if (!currentLesson) return undefined;

      const updatedMetadata = { ...(currentLesson.metadata ?? {}), banner: imageUploaded };

      return {
        ...currentLesson,
        metadata: updatedMetadata as ContentMetadata,
      };
    });
  }

  // I want to deprecate this method

  /**
   * @deprecated Use create new method.
   * Generates lesson content using AI. Assumes the lesson is already saved.
   * @param lessonId The ID of the lesson to generate content for.
   * @returns The updated lesson object after AI generation, or null on failure.
   */
  public async generateByAI(lessonId: string): Promise<ILesson | null> {
    if (!lessonId) {
      this.#toastService.warn({ title: 'ID Requerido', subtitle: 'Se necesita un ID de lección para usar IA.' });
      return null;
    }

    // No need to save here, component should ensure it's saved before calling.
    try {
      await this.#lessonService.postGenerateByAI(lessonId);
      // Re-fetch the lesson data to get AI updates
      const updatedLesson = await this.#lessonService.getLesson(lessonId);
      if (updatedLesson) {
        this.#toastService.success({ title: 'IA completada', subtitle: 'Lección actualizada con IA.' });
        return updatedLesson;
      } else {
        throw new Error('Failed to fetch lesson after AI generation');
      }
    } catch (error: any) {
      // Type the error object
      console.error('Error during AI generation in service:', error);
      this.#toastService.error({ title: 'Error de IA', subtitle: 'No se pudo generar la lección con IA.' });
      return null; // Return null in catch block
    }
    // Loading state should be managed by the component calling this service.
  }

  /**
   * Improves the provided Markdown text using AI and updates the lesson.
   * Assumes the lesson is already saved before calling this.
   * @param lesson The lesson object containing the prompt and other context.
   * @param markdownText The Markdown text generated from the lesson's HTML content.
   * @returns The improved Markdown string, or null on failure.
   */
  public async improveMDWithAI(lesson: ILesson, markdownText: string): Promise<string | null> {
    if (!markdownText) {
      this.#toastService.warn({ title: 'Texto Requerido', subtitle: 'Se necesita texto Markdown para mejorar con IA.' });
      return null;
    }

    try {
      this.loadingBarService.showIndeterminate();

      const textPrompt = this.#lessonService.getPrompts().content(lesson);

      const messages = [{ content: textPrompt, role: ChatRole.User }];

      const response = await this.#agentService.callChatCompletion({ messages, model: { provider: 'google' } });
      let improvedMarkdown = response.content?.trim() ?? null;

      // Remove potential markdown fences
      const markdownFenceStart = '```markdown\n';
      const markdownFenceEnd = '\n```';
      if (improvedMarkdown?.startsWith(markdownFenceStart) && improvedMarkdown.endsWith(markdownFenceEnd)) {
        improvedMarkdown = improvedMarkdown.slice(markdownFenceStart.length, -markdownFenceEnd.length);
      } else {
        // Also handle cases where it might just be ``` at the start/end
        const simpleFence = '```';
        if (improvedMarkdown?.startsWith(simpleFence)) {
          improvedMarkdown = improvedMarkdown.slice(simpleFence.length);
        }
        if (improvedMarkdown?.endsWith(simpleFence)) {
          improvedMarkdown = improvedMarkdown.slice(0, -simpleFence.length);
        }
      }
      // Trim any leading/trailing whitespace that might remain after removing fences
      improvedMarkdown = improvedMarkdown?.trim() ?? null;

      console.log('Improved Markdown:', improvedMarkdown);

      return improvedMarkdown; // Return only the string
    } catch (error: any) {
      console.error('Error during AI Markdown improvement in service:', error);
      this.#toastService.error({ title: 'Error de IA', subtitle: 'No se pudo mejorar la lección con IA.' });
      return null; // Return null in catch block
    } finally {
      this.loadingBarService.successAndHide();
    }
  }

  /**
   * Generates a concise description for the lesson using AI based on its content.
   * @param lesson The lesson object containing the content.
   * @returns A promise resolving to the generated description string, or null on failure.
   */
  public async generateDescriptionWithAI(lesson: ILesson): Promise<string | null> {
    if (!lesson || !lesson.textCoded) {
      this.#toastService.warn({ title: 'Contenido Requerido', subtitle: 'Se necesita contenido en la lección para generar una descripción.' });
      return null;
    }

    try {
      this.loadingBarService.showIndeterminate(); // Corrected: No argument needed

      // Extract plain text from the lesson's HTML content
      const plainTextContent = this._extractTextFromHtml(lesson.textCoded);
      if (!plainTextContent || plainTextContent.trim().length === 0) {
        this.#toastService.warn({ title: 'Texto Vacío', subtitle: 'No se pudo extraer texto útil del contenido de la lección.' });
        return null;
      }

      const descriptionPrompt = this.#lessonService.getPrompts().description(lesson);

      const messages = [{ content: descriptionPrompt, role: ChatRole.User }];

      const response = await this.#agentService.callChatCompletion({ messages, model: { provider: 'google' } });

      let generatedDescription = response.content?.trim() ?? null;

      // Basic cleanup (remove potential quotes if the AI wraps the response)
      if (generatedDescription && generatedDescription.startsWith('"') && generatedDescription.endsWith('"')) {
        generatedDescription = generatedDescription.substring(1, generatedDescription.length - 1);
      }

      if (generatedDescription) {
        this.#toastService.success({ title: 'Descripción Generada', subtitle: 'Se generó la descripción con IA.' });
        return generatedDescription;
      } else {
        throw new Error('AI did not return a valid description.');
      }
    } catch (error: any) {
      console.error('Error during AI description generation in service:', error);
      this.#toastService.error({ title: 'Error de IA', subtitle: 'No se pudo generar la descripción con IA.' });
      return null;
    } finally {
      this.loadingBarService.successAndHide();
    }
  }

  /**
   * Extracts plain text from an HTML string, removing encoded JSON components.
   * @param htmlInput The HTML string potentially containing encoded JSON (~...~).
   * @returns The plain text representation.
   */
  private _extractTextFromHtml(htmlInput: string): string {
    if (!htmlInput) return '';

    // 1. Replace encoded JSON blocks with their 'settings.text' or an empty string
    const jsonRegex = /~(.+?)~/g;
    const textWithPlaceholders = htmlInput.replace(jsonRegex, (match, jsonCoded) => {
      try {
        const decodedJson = jsonCoded.replace(/"/g, '"').replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>');
        const data = JSON.parse(decodedJson);
        return data?.settings?.text || ''; // Return text or empty string if not found
      } catch (error) {
        console.error('Failed to parse JSON inside HTML during text extraction:', jsonCoded, error);
        return ''; // Return empty string on error
      }
    });

    // 2. Strip remaining HTML tags to get plain text
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = textWithPlaceholders;
    // Use textContent to get the plain text, effectively stripping tags
    // Replace multiple whitespace characters (including newlines) with a single space
    return (tempDiv.textContent || tempDiv.innerText || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Cleans orphaned components from the lesson's dynamicComponents.
   * An orphaned component is one present in dynamicComponents but its ID is not found in the textCoded HTML.
   * @param lesson The lesson object to clean.
   * @returns A new lesson object with orphaned components removed from dynamicComponents.
   */
  public cleanOrphanedComponents(lesson: ILesson): ILesson {
    if (!lesson || !lesson.textCoded || !lesson.dynamicComponents) {
      // Return the original lesson if essential parts are missing
      return lesson;
    }

    const textCoded = lesson.textCoded;
    const existingComponents = lesson.dynamicComponents;
    const existingComponentIds = new Set(Object.keys(existingComponents));

    // Regex to find "id":"<component_id>" within the textCoded string
    const idRegex = /"id":"([^"]+)"/g;
    const foundIdsInText = new Set<string>();
    let match;

    while ((match = idRegex.exec(textCoded)) !== null) {
      const potentialId = match[1];
      // Check if the found ID actually exists in our dynamic components map
      // This ensures we only count IDs that correspond to known components.
      if (existingComponentIds.has(potentialId)) {
        foundIdsInText.add(potentialId);
      }
    }

    const orphanedIds: string[] = [];
    const cleanedDynamicComponents: Record<string, DynamicContentComponent> = {};

    // Iterate through existing component IDs
    for (const componentId of existingComponentIds) {
      if (foundIdsInText.has(componentId)) {
        // Keep the component if its ID was found in the text
        cleanedDynamicComponents[componentId] = existingComponents[componentId];
      } else {
        // Mark as orphaned if not found
        orphanedIds.push(componentId);
      }
    }

    if (orphanedIds.length > 0) {
      console.warn(`[LessonUtilsService] Orphaned components detected and will be removed from lesson data (IDs not found in textCoded):`, orphanedIds);
      this.#toastService.warn({
        title: 'Componentes Huérfanos Detectados',
        subtitle: `Se removerán ${orphanedIds.length} componentes no usados del editor.`,
        // life: 5000, // Removed 'life' property as it might not be supported by ToastData
      });
    }

    // Return a new lesson object with the cleaned components
    return {
      ...lesson,
      dynamicComponents: cleanedDynamicComponents,
    };
  }
}
